import { MapPin, Pencil, Trash2 } from "lucide-react";

export interface RoundPlanTableRow {
  // Stable identifier the parent keys the edit action off of: the round number
  // for a generated (create) round, the backend round id for a saved one.
  id: string;
  roundNumber: number;
  courseName: string;
  windowLabel: string;
  windowSub?: string;
  holesLabel: string;
  custom?: boolean;
  // Rounds that can't be represented in the drawer yet (e.g. a Flex round with
  // no play window) omit the edit control rather than open an unsavable form.
  editable?: boolean;
  // A Finalized saved round (or one with no real id yet) can't be removed;
  // defaults to true so a generated (create) row needs no extra plumbing.
  removable?: boolean;
}

const th =
  "px-3 py-2 text-left text-xs font-medium uppercase tracking-wide poppins text-text-muted";
const td = "px-3 py-2.5 align-middle text-sm mulish";

export function RoundPlanTable({
  rows,
  onEdit,
  onRemove,
  removeLabel = "Remove",
}: {
  rows: RoundPlanTableRow[];
  onEdit: (id: string) => void;
  // Omitted entirely when the caller offers no way to remove a round (kept
  // out of the create-only tests below by simply not passing it).
  onRemove?: (id: string) => void;
  // Prefixes the accessible name ("<removeLabel> round N"): "Remove" for a
  // not-yet-saved generated round, "Archive" for a saved one.
  removeLabel?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full border-collapse">
        <thead className="border-b border-border bg-muted/40">
          <tr>
            <th className={th} aria-hidden />
            <th className={th}>Play Window</th>
            <th className={th}>Course</th>
            <th className={th}>Holes</th>
            <th className={th} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-border last:border-0 transition-colors hover:bg-muted/40"
            >
              <td className={`${td} w-10 text-text-muted`}>
                <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold monoNumber tabular-nums">
                  {r.roundNumber}
                </span>
              </td>
              <td className={td}>
                <div className="font-medium text-text-primary">
                  {r.windowLabel}
                </div>
                {r.windowSub && (
                  <div className="text-xs text-text-muted">{r.windowSub}</div>
                )}
              </td>
              <td className={td}>
                <span className="flex items-center gap-1.5 font-medium text-text-primary">
                  <MapPin className="size-3.5 shrink-0 text-text-muted" />
                  {r.courseName || "Select course"}
                  {r.custom && (
                    <span className="rounded-full bg-secondary/15 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary poppins">
                      Custom
                    </span>
                  )}
                </span>
              </td>
              <td className={`${td} text-text-muted`}>{r.holesLabel}</td>
              <td className={`${td} w-10`}>
                <div className="flex items-center gap-1">
                  {r.editable !== false && (
                    <button
                      type="button"
                      onClick={() => onEdit(r.id)}
                      aria-label={`Edit round ${r.roundNumber}`}
                      className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-muted hover:text-text-primary"
                    >
                      <Pencil className="size-4" />
                    </button>
                  )}
                  {onRemove && (
                    <button
                      type="button"
                      onClick={() => onRemove(r.id)}
                      disabled={r.removable === false}
                      aria-label={`${removeLabel} round ${r.roundNumber}`}
                      className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-muted hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
